import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { isFunction } from 'lodash';
import classnames from 'classnames';

import { CoreSelectBox } from 'components/core/library/selectBox';
import { Text } from 'components/core/Typography';
import ChevronDown from 'components/svg/ChevronDown';

import './selectBox.scss';

const SelectBox = ({
	id,
	disabled,
	label,
	large,
	className,
	reduxFormError,
	errorMessage,
	options = [],
	value: valueProp,
	validator,
	onChange,
	isModal,
}) => {
	const [value, setValue] = useState(valueProp);
	const [hasError, setHasError] = useState(false);
	const prevValueProp = useRef(valueProp);

	const handleChange = useCallback(
		(evt) => {
			const { value: newValue } = evt.target;
			const newHasError =
				isFunction(validator) && newValue.length
					? validator(newValue)
					: false;
			setValue(newValue);
			setHasError(newHasError);

			if (isFunction(onChange)) {
				onChange(evt);
			}
		},
		[validator, onChange],
	);

	useEffect(() => {
		if (prevValueProp.current !== valueProp) {
			setValue(valueProp);
		}
		prevValueProp.current = valueProp;
	}, [valueProp]);

	return (
		<label
			htmlFor={id}
			className={classnames('ph-label', 'select-box-label', {
				'label-error-datepicker':
					(hasError || reduxFormError) && isModal,
			})}
		>
			<Text
				size="small"
				className="selected-value"
			>
				{options.find((o) => o.value === value)?.display}
			</Text>
			<CoreSelectBox
				className={classnames(className, {
					'select-input-large': large,
					'select-input-standard': !large,
					disabled,
					error: hasError || reduxFormError,
				})}
				disabled={disabled}
				id={id}
				label={label}
				onChange={handleChange}
				value={value}
				key="core-selectbox"
				options={options}
			/>
			<div className="dropdown-icon">
				<ChevronDown />
			</div>
			<div
				className={classnames(
					{ 'margin-error-datepicker': isModal },
					'input-error',
				)}
				key="input-error"
			>
				{hasError || reduxFormError ? errorMessage : null}
			</div>
		</label>
	);
};

SelectBox.propTypes = {
	className: PropTypes.string,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.any,
			display: PropTypes.any,
		}),
	),
	value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	large: PropTypes.bool,
	disabled: PropTypes.bool,
	label: PropTypes.node,
	id: PropTypes.string.isRequired,
	errorMessage: PropTypes.node,
	reduxFormError: PropTypes.bool,
	isModal: PropTypes.bool,
	validator: PropTypes.func,
	onChange: PropTypes.func,
};

export default SelectBox;
